import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStyletron } from 'baseui'
import { StyledLoadingSpinner } from 'baseui/button'
import { Cell, Grid } from 'baseui/layout-grid'
import { FormControl } from 'baseui/form-control'
import { ModalHeader, ModalBody, ModalButton } from 'baseui/modal'
import { StyledTable } from 'baseui/table'
import { StyledBodyCell } from 'baseui/table-grid'
import { Textarea, SIZE } from 'baseui/textarea'

export interface CommentItem {
  comment: string
  created_at?: string
  createdAt?: string
  user?: { firstname?: string; name?: string }
}

interface CommentsModalContentProps {
  comments: CommentItem[]
  onSubmit: (comment: string, callback: (success: boolean) => void) => void
  isLoadingComments?: boolean
}

const CommentsModalContent = ({ comments, onSubmit, isLoadingComments = false }: CommentsModalContentProps) => {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setLoading] = useState(false)

  const displayContentStyle = css({ display: 'contents' })
  const commentStyle = css({ fontSize: '.75rem', fontStyle: 'italic' })
  const dateStyle = css({
    display: 'block',
    width: '100%',
    textAlign: 'right' as const,
    fontSize: '.75rem',
    fontWeight: 'bold',
  })

  const handleResponse = (success: boolean) => {
    setLoading(false)
    if (success) {
      setComment('')
      return
    }
    setError(t('subscriptionTable:save_error'))
  }

  const saveCommentHandler = () => {
    setLoading(true)
    setError('')
    if (comment.length === 0) {
      setError(t('subscriptionTable:mandatory_field'))
      setLoading(false)
      return
    }
    onSubmit(comment, handleResponse)
  }

  const reversedComments = [...comments].reverse()

  return (
    <>
      <ModalHeader>{t('subscriptionTable:comments')}</ModalHeader>
      <ModalBody>
        <Grid>
          <Cell span={12}>
            <div className={css({ height: '250px' })}>
              <StyledTable role="grid">
                {isLoadingComments ? (
                  <div className={css({ display: 'flex', justifyContent: 'center', padding: '2rem' })}>
                    <StyledLoadingSpinner />
                  </div>
                ) : reversedComments.length === 0 ? (
                  <div role="row" className={displayContentStyle}>
                    <StyledBodyCell>
                      <span className={commentStyle}>{t('subscriptionTable:no_comments')}</span>
                    </StyledBodyCell>
                  </div>
                ) : (
                  reversedComments.map((row, index) => {
                    const date = (row.created_at || row.createdAt)
                      ? new Date(row.created_at || row.createdAt || '').toLocaleString('fr-FR')
                      : ''
                    const createdBy =
                      row.user ? `${row.user.firstname ?? ''} ${row.user.name ?? ''}`.trim() : ''

                    return (
                      <div role="row" className={displayContentStyle} key={index}>
                        <StyledBodyCell $striped={index % 2 === 0}>
                          <span className={commentStyle}>{row.comment}</span>
                          <span className={dateStyle}>{date}</span>
                          {createdBy && <span className={dateStyle}>{createdBy}</span>}
                        </StyledBodyCell>
                      </div>
                    )
                  })
                )}
              </StyledTable>
            </div>
          </Cell>
          <Cell span={12}>
            <FormControl label={t('subscriptionTable:add_comment')} error={error}>
              <Textarea
                value={comment}
                onChange={e => setComment((e.target as HTMLTextAreaElement).value)}
                size={SIZE.mini}
                placeholder={t('subscriptionTable:write_comment')}
                clearable
              />
            </FormControl>
            <FormControl>
              <ModalButton
                className={css({ width: '100%' })}
                isLoading={isLoading}
                onClick={saveCommentHandler}
                size={SIZE.mini}
              >
                {t('subscriptionTable:validate_comment')}
              </ModalButton>
            </FormControl>
          </Cell>
        </Grid>
      </ModalBody>
    </>
  )
}

export default CommentsModalContent
